/* utils/revKeys.js -- W-082 (2026-09-30): THE REVENUE-LINE RULES, ONE OWNER.
 *
 * Which income-statement line is a computed row, which looks like revenue, how the workbook lines are grouped, and the key a
 * revenue line is booked under. Three callers read these SAME functions:
 *   - the Step-4 import (AdminPage.js buildFinancialsPayload) -- the is_actuals keys the model reads;
 *   - the Step-3 save (AdminPage.js buildStep3LeafKeyResolver, W-078) -- the key a new line is pinned with at birth;
 *   - the Step-4b review bridge (computeHistoricalFinancials.js) -- the review screen's revenue rows and pre-flight P13.
 * The bridge carried its OWN copy of the key rule, without the reservation W-078 added, so a new line whose name slugged
 * like a pinned one collapsed onto the pin in the review screen (diffs/group1-checks-phase0.txt section 4). That copy is
 * retired: the bridge calls buildRevKeyResolver.
 *
 * MOVED VERBATIM: the predicates (REV_KEYWORDS, COMPUTED_EXACT, isComputedRow, isExcludedComputedRow, REV_KEYWORDS_RE) from
 * computeHistoricalFinancials.js -- which re-exports them, so every existing import is unchanged -- with the two em-dashes in
 * their comments written as "--"; groupGLLines, reservedLeafKeys and buildRevKeyResolver from AdminPage.js. This module
 * imports neither of those files, so neither can form an import cycle through it. */
import { leafKeyFor } from './leafKey';
import { normalizeBSKey } from './bsKey';
import { matchPayrollRole } from './inferIsMeta';
import { looksLikeTotalRow } from './templateHeaders';

const r2 = n => Math.round(n * 100) / 100;

const REV_KEYWORDS = /revenue|\bincome\b|sales|refund|discount|billable|professional development|other revenue|self serve|self-serve|enterprise/i;

// Computed rows imported as GL lines -- must be filtered out before section assignment
const COMPUTED_EXACT = new Set([
    'net income', 'net loss', 'net income (loss)', 'net income / loss', 'net loss / income',
    'net (loss) income', 'net loss (income)',
    'gross profit', 'gross margin', 'gross loss', 'gross profit (loss)',
    'operating income', 'operating loss', 'operating profit', 'operating income (loss)',
    'income from operations', 'loss from operations',
    'ebitda', 'ebit',
    'income before taxes', 'loss before taxes', 'income (loss) before taxes',
    'income tax expense', 'provision for income taxes',
]);

export function isComputedRow(gl) {
    const raw = (gl.gl_name || '').trim();
    if (!raw) return true; // blank/empty gl_name -- exclude
    const name = raw.toLowerCase();
    if (COMPUTED_EXACT.has(name)) return true;
    if (/^total\b/.test(name)) return true;       // "Total ...", "Total Revenue", "Total COGS"
    if (/^subtotal\b/.test(name)) return true;
    if (/^grand total\b/.test(name)) return true;
    if (/\btotal\b/.test(name) && name !== 'total') return true;
    // Extended patterns to catch computed P&L rows that may carry suffixes
    // like "(Reported)", "Adjusted ...", or be margin % rows.
    if (/\bebitda\b/.test(name)) return true;                    // "EBITDA (Reported)", "Adjusted EBITDA"
    if (/\bebit\b/.test(name) && !/\bdebit\b/.test(name)) return true;
    if (/^gross (profit|loss|margin)\b/.test(name)) return true;
    if (/^operating (income|loss|profit|margin)\b/.test(name)) return true;
    if (/^net (income|loss|profit)\b/.test(name)) return true;
    if (/\b(gp|gross|ebit|ebitda|operating|net|profit)\s+margin\b/.test(name)) return true;
    if (/^income (before|from) /.test(name)) return true;
    if (/^loss (before|from) /.test(name)) return true;
    return false;
}

/* W-068 pass 3 -- A COMPUTED-LOOKING ROW IS EXCLUDED, UNLESS IT IS A TOTAL-LOOKING ROW THE OPERATOR MAPPED.
 * The template upload now KEEPS a row whose name contains "total" (F1) and Step 3 pre-sets it to Skip, marked
 * suggested. If the operator instead maps it to a real section ("Total Rewards Platform" is a real G&A line), it must be
 * booked like any other line -- in the Step-4 is_actuals payload (the MODEL) and in this bridge (the review screen and
 * pre-flight P13) alike. isComputedRow alone excluded it in both, whatever the mapping: the line would be silently lost.
 * ONLY the total-looking names get this exception; every other computed name (Gross Profit, Net Income, EBITDA ...,
 * incl. the BS "Net Income" equity row) is excluded exactly as before, mapped or not. ONE predicate, three readers. */
export function isExcludedComputedRow(gl, isCategory) {
    if (!isComputedRow(gl)) return false;
    const mapped = !!isCategory && String(isCategory).trim() !== '' && isCategory !== 'Skip';
    return !(mapped && looksLikeTotalRow(gl.gl_name));
}

export const REV_KEYWORDS_RE = REV_KEYWORDS;

/* The Step-4 import's grouping of historical_gl_lines rows -- one group per (statement_type, gl_name, gl_category), in
 * workbook sort_order. Moved from AdminPage.js; the ORDER is load-bearing (the `_${n}` dedupe numbers lines in it). */
export function groupGLLines(glLines) {
    const groups = new Map();
    for (const row of glLines || []) {
        const key = `${row.statement_type}|${row.gl_name}|${row.gl_category || ''}`;
        if (!groups.has(key)) {
            groups.set(key, {
                statement_type: row.statement_type,
                gl_name: row.gl_name,
                gl_category: row.gl_category || '',
                sort_order: row.sort_order == null ? 999999 : row.sort_order,
                amounts: {},
            });
        }
        const g = groups.get(key);
        g.amounts[row.month] = r2((g.amounts[row.month] || 0) + (parseFloat(row.amount) || 0));
    }
    return Array.from(groups.values()).sort((a, b) => {
        if (a.statement_type !== b.statement_type) return a.statement_type < b.statement_type ? -1 : 1;
        return a.sort_order - b.sort_order;
    });
}

/* W-078 (2026-09-30) -- every key another line already holds: the pinned identity (leaf_key) and the mapped IS
 * identity (is_line_key) of EVERY row, whatever its statement, because the Step-3 save's server rule is
 * company-wide (a new leaf_key may not equal another row's leaf_key or is_line_key -- leaf_key_conflict). A
 * derived key must never take one: two lines on one key are MERGED silently by the import. On a fully pinned
 * tenant nothing is derived, and on a fully unpinned one no derived key is in this set (measured on all five
 * tenants with their pins stripped, diffs/w078-build.txt), so the keys are unchanged; it decides only the MIXED
 * case -- a new line on a tenant whose other lines are already pinned. */
export function reservedLeafKeys(mappings) {
    const used = new Set();
    for (const m of mappings || []) {
        for (const k of [m.leaf_key, m.is_line_key]) if (k !== null && k !== undefined && k !== '') used.add(k);
    }
    return used;
}

/* W-078 (2026-09-30) -- THE ONE revenue-leaf key derivation. It lived only INLINE in buildFinancialsPayload, so
 * the Step-3 save had nothing to call; extracted here, and the import now calls it too (one owner, two callers).
 * An IS line is booked under this key when it has NO is_line_key and is:
 *   - not a computed row (isExcludedComputedRow -- the ONE predicate the Step-4b bridge applies too);
 *   - Skip'd or unmapped (a line with any other category and no key books nothing here);
 *   - NOT payroll -- tested FIRST (matchPayrollRole, the ONE shared matcher). A Skip'd payroll row's name or
 *     section ("Sales Compensation" / "Sales and Marketing") matches REV_KEYWORDS_RE on `sales`, so tested
 *     second it was booked as revenue (measured on cid=8: rev_sales_compensation = $4,918,004.30). The Step-5c
 *     back-fill (derivePerDgPayrollActuals) claims payroll rows instead;
 *   - looks like revenue (REV_KEYWORDS_RE on gl_name or gl_category).
 * Its key: the PINNED key (leafKeyFor -- leaf_key, the line's stored identity) wins, so a rename never detaches
 * its history, its revenue_module_mappings row or a DSO base that reads it; otherwise the DERIVE FALLBACK --
 * `rev_${normalizeBSKey(gl_name)}` with a `_${n}` dedupe in group order -- which is now the key the Step-3 save
 * PINS at birth, so a new line is pinned before it is ever imported. Returns (glName, glCategory) => key|null. */
export function buildRevKeyResolver(glLines, mappings) {
    const mlookup = new Map();
    for (const m of mappings || []) mlookup.set(`${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`, m);
    const used = reservedLeafKeys(mappings);
    const counts = new Map();
    const byLine = new Map();
    for (const g of groupGLLines(glLines)) {
        if (g.statement_type !== 'IS') continue;
        const m = mlookup.get(`IS|${g.gl_name}|${g.gl_category || ''}`);
        if (isExcludedComputedRow(g, m?.is_category)) continue;
        if (m && m.is_line_key) continue;
        const cat = m?.is_category || null;
        if (cat && cat !== 'Skip') continue;
        if (matchPayrollRole(g.gl_name)) continue;
        if (!(REV_KEYWORDS_RE.test(g.gl_name || '') || REV_KEYWORDS_RE.test(g.gl_category || ''))) continue;
        const pinned = leafKeyFor(m);
        if (pinned) {
            counts.set(pinned, Math.max(counts.get(pinned) || 0, 1));
            byLine.set(`${g.gl_name}|${g.gl_category || ''}`, pinned);
            continue;
        }
        const base = `rev_${normalizeBSKey(g.gl_name)}`;
        if (!base || base === 'rev_') continue;
        let n = (counts.get(base) || 0) + 1;
        let key = n === 1 ? base : `${base}_${n}`;
        while (used.has(key)) { n++; key = `${base}_${n}`; }
        counts.set(base, n);
        used.add(key);
        byLine.set(`${g.gl_name}|${g.gl_category || ''}`, key);
    }
    return (glName, glCategory) => byLine.get(`${glName}|${glCategory || ''}`) || null;
}
